import { Eye, EyeOff, ChevronUp, ChevronDown, ShoppingCart, ExternalLink } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import type { ChecklistItem, SortColumn, SortDirection } from '../types';
import { getIkeaImageUrl, getIkeaProductUrl } from '../utils/helpers';

interface ChecklistTableProps {
  items: ChecklistItem[];
  sortColumn: SortColumn;
  sortDirection: SortDirection;
  onSort: (column: SortColumn) => void;
  onItemClick: (item: ChecklistItem) => void;
  onToggleWatchlist: (itemId: string) => void;
  checklistId: string;
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useLanguage();

  switch (status) {
    case 'installed':
      return (
        <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
          {t('checklists.statusInstalled', 'Installed')}
        </span>
      );
    case 'purchased':
      return (
        <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
          {t('checklists.statusPurchased', 'Purchased')}
        </span>
      );
    default:
      return (
        <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
          {t('checklists.statusPending', 'Pending')}
        </span>
      );
  }
}

function SortIcon({ column, currentColumn, direction }: {
  column: SortColumn;
  currentColumn: SortColumn;
  direction: SortDirection;
}) {
  if (column !== currentColumn) return null;
  return direction === 'asc'
    ? <ChevronUp className="w-4 h-4" />
    : <ChevronDown className="w-4 h-4" />;
}

export function ChecklistTable({
  items,
  sortColumn,
  sortDirection,
  onSort,
  onItemClick,
  onToggleWatchlist,
}: ChecklistTableProps) {
  const { t } = useLanguage();

  if (items.length === 0) return null;

  return (
    <div className="overflow-hidden">
      {/* Table Header */}
      <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-gray-50 text-xs font-medium text-gray-500 uppercase border-b border-gray-100 sticky top-0 z-10">
        <div className="col-span-1 flex items-center">
          <Eye className="w-4 h-4" />
        </div>
        <button
          onClick={() => onSort('code')}
          className="col-span-2 flex items-center gap-1 hover:text-gray-700 transition-colors"
        >
          {t('checklists.code', 'Code')}
          <SortIcon column="code" currentColumn={sortColumn} direction={sortDirection} />
        </button>
        <button
          onClick={() => onSort('name')}
          className="col-span-5 flex items-center gap-1 hover:text-gray-700 transition-colors"
        >
          {t('checklists.productName', 'Product')}
          <SortIcon column="name" currentColumn={sortColumn} direction={sortDirection} />
        </button>
        <button
          onClick={() => onSort('qty')}
          className="col-span-2 flex items-center gap-1 hover:text-gray-700 transition-colors"
        >
          {t('checklists.qty', 'Qty')}
          <SortIcon column="qty" currentColumn={sortColumn} direction={sortDirection} />
        </button>
        <div className="col-span-2">
          {t('checklists.status', 'Status')}
        </div>
      </div>

      {/* Table Rows */}
      <div className="divide-y divide-gray-100">
        {items.map((item, index) => {
          const progress = item.quantityNeeded > 0
            ? ((item.quantityPurchased || 0) / item.quantityNeeded) * 100
            : 0;

          return (
            <button
              key={item.id}
              onClick={() => onItemClick(item)}
              className="w-full grid grid-cols-12 gap-2 px-4 py-3 text-left group transition-colors hover:bg-primary-50 animate-fade-in"
              style={{ animationDelay: `${index * 30}ms` }}
            >
              {/* Watchlist toggle */}
              <div className="col-span-1 flex items-center">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (item.productCode) {
                      onToggleWatchlist(item.id);
                    }
                  }}
                  className={`p-1 rounded-lg transition-colors ${
                    item.onWatchlist
                      ? 'text-primary-600 hover:bg-primary-100'
                      : item.productCode
                        ? 'text-gray-300 hover:text-primary-500 hover:bg-gray-100'
                        : 'text-gray-200 cursor-not-allowed'
                  }`}
                  aria-label={item.onWatchlist
                    ? t('checklists.removeFromWatchlist', 'Remove from watchlist')
                    : t('checklists.addToWatchlist', 'Add to watchlist')
                  }
                  disabled={!item.productCode}
                >
                  {item.productCode ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                </button>
              </div>

              {/* Product Code */}
              <div className="col-span-2 flex items-center text-sm text-gray-500 font-mono truncate">
                {item.productCode || '-'}
              </div>

              {/* Product Name + Thumbnail */}
              <div className="col-span-5 flex items-center gap-3">
                {item.productCode && (
                  <img
                    src={getIkeaImageUrl(item.productCode)}
                    alt=""
                    className="w-10 h-10 rounded-lg object-contain bg-gray-100 flex-shrink-0"
                    loading="lazy"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-gray-900 truncate block group-hover:text-primary-700 transition-colors">
                    {item.name}
                  </span>
                </div>
              </div>

              {/* Quantity with progress */}
              <div className="col-span-2 flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  {item.quantityPurchased || 0}/{item.quantityNeeded}
                </span>
                <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden max-w-[60px]">
                  <div
                    className={`h-full rounded-full transition-all ${
                      progress >= 100 ? 'bg-green-500' : 'bg-primary-600'
                    }`}
                    style={{ width: `${Math.min(100, progress)}%` }}
                  />
                </div>
              </div>

              {/* Status + Hover Actions */}
              <div className="col-span-2 flex items-center justify-between">
                <StatusBadge status={item.status} />

                {/* Inline actions - appear on hover */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {item.productCode && (
                    <a
                      href={getIkeaProductUrl(item.productCode)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="p-1.5 rounded-lg hover:bg-white text-gray-400 hover:text-primary-600 transition-colors"
                      title={t('checklists.viewOnIkea', 'View on IKEA')}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                  {(item.quantityPurchased || 0) < (item.quantityNeeded || 1) && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onItemClick(item);
                      }}
                      className="p-1.5 rounded-lg hover:bg-white text-gray-400 hover:text-primary-600 transition-colors"
                      title={t('checklists.recordPurchase', 'Record Purchase')}
                    >
                      <ShoppingCart className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
